export const theme = {
  primary: '#0a6e4e',
  primaryLight: '#e8f8ef',
  shadow: 'rgba(10, 110, 78, 0.08)',
  gradient: 'linear-gradient(125deg, #0a2e22 0%, #0a5c42 42%, #0a6e4e 100%)',
  overlay: 'radial-gradient(ellipse 50% 60% at 85% 40%, rgba(120, 220, 160, 0.15) 0%, transparent 55%)'
}

export const pageMeta = {
  title: '数字乡村管理系统',
  badge: '数字化赋能乡村振兴',
  description:
    '融合物联网、大数据、移动互联网与AI技术，打造乡村治理、智慧农业、便民服务、产业振兴全流程一体化数字乡村平台，助力乡村振兴战略落地见效。',
  stats: [
    { value: '治理', label: '乡村数字化' },
    { value: '农业', label: '智慧监测' },
    { value: '便民', label: '服务下沉' },
    { value: '100%', label: '安全合规' }
  ]
}

export const sections = {
  features: {
    title: '核心功能',
    desc: '六大功能模块覆盖数字乡村建设全流程，以数字化技术驱动乡村治理现代化与产业振兴。'
  },
  whyUs: {
    title: '为什么选择我们',
    desc: '深耕乡村数字化建设领域多年，以技术实力与落地经验赢得客户信赖。'
  },
  related: {
    title: '相关产品',
    desc: '探索更多小智科技政务数字化解决方案。'
  }
}

export const features = [
  {
    icon: '🏘️',
    title: '乡村治理数字化',
    desc: '整合网格管理、事件上报、民情日记等功能，实现"人在格中走、事在格中办"，提升乡村基层治理精细化水平。'
  },
  {
    icon: '🌾',
    title: '智慧农业监测',
    desc: '对接气象、土壤、虫情等物联网传感器，实时监测农业生产环境，提供种植建议与预警，助力农业增产增收。'
  },
  {
    icon: '📱',
    title: '便民服务门户',
    desc: '整合政务办事、政策查询、农产品销售、乡村旅游等便民服务，村民通过手机即可办理常用事项，服务下沉到村到户。'
  },
  {
    icon: '📹',
    title: '平安乡村监控',
    desc: '对接视频监控、人脸识别等设备，实现重点区域全天候监控，支持异常事件自动告警，守护乡村平安。'
  },
  {
    icon: '🏭',
    title: '产业振兴管理',
    desc: '跟踪特色产业发展、合作社运营、扶贫项目进展，可视化呈现产业数据，辅助乡镇政府精准施策。'
  },
  {
    icon: '🖥️',
    title: '乡村数据驾驶舱',
    desc: '一屏掌握人口、土地、产业、环境等乡村关键指标，为乡镇党委政府决策提供数据支撑。'
  }
]

export const architecture = {
  variant: 'village',
  title: '数字乡村管理系统',
  desc: '以"服务应用领域—数据库与平台—接入来源"三层架构为核心，融合党建引领、精细治理、便民服务与特色产业，构建安全可控、灵活扩展的数字乡村解决方案。',
  applications: [
    {
      title: '党建引领',
      items: [
        '组织建设标准化',
        '全周期管理',
        '组织生活常态化',
        '先锋带头示范化',
        '组织活动动态化',
        '党员双报到'
      ]
    },
    {
      title: '精细化管理',
      items: [
        '全要素网格',
        '事件流转',
        '综治',
        '重点人员管控',
        '治安事件',
        '综合执法'
      ]
    },
    {
      title: '个性化服务',
      items: [
        '办事指南',
        '信息公开',
        '附近商家',
        '便民信息',
        '智慧养老',
        '疫情防控'
      ]
    },
    {
      title: '特色产业',
      items: [
        '土地管理',
        '农村电商',
        '美丽乡村',
        '林业管理',
        '特色种植',
        '特色养殖'
      ]
    }
  ],
  dataLayer: {
    databases: [
      {
        title: '一标三实数据库',
        items: ['标准地址', '实有人口', '实有房屋', '实有单位']
      },
      {
        title: '基层治理数据库',
        items: ['人', '地', '物', '事', '情', '组织']
      }
    ],
    platforms: [
      '第三方信息双向接入平台',
      '5G+物联网接入平台',
      'AI+视联网接入平台'
    ]
  },
  sources: [
    {
      title: '上级应用',
      items: ['大数据局', '组织部', '政法委', '住建局', '民政局']
    },
    {
      title: '下级应用',
      items: ['小区', '村民', '网格']
    },
    {
      title: '硬件',
      items: ['监控', '大喇叭', '智能手环', '水文监测', '气象检测']
    }
  ],
  footer: '物联融合 · 数据共享 · 等保测评 · 私有化部署'
}

export const whyUs = [
  {
    title: '政策契合',
    desc: '全面对接乡村振兴战略与数字乡村发展规划，功能设计符合农业农村部、民政部等相关政策要求。'
  },
  {
    title: '因地制宜',
    desc: '支持按乡镇、村庄实际情况灵活配置功能模块，既有标准产品快速上线，也支持个性化定制开发。'
  },
  {
    title: '实战验证',
    desc: '已在湖北省多个乡镇、行政村成功落地，覆盖鄂西北山区、平原等多种地理形态，经受真实场景检验。'
  },
  {
    title: '持续运营',
    desc: '提供专属实施团队、村民培训与7×24运维保障，确保系统真正用起来、持续发挥效益。'
  }
]

export const scenarios = {
  label: '乡村落地场景',
  title: '让乡村治理、农业监测和便民服务更有章法',
  desc: '系统定位为"数字乡村工具"，帮助乡镇干部和村干部从繁琐事务中解放出来，提升服务村民的效率与质量。',
  items: [
    {
      title: '网格治理精细化',
      desc: '划分责任网格，村民随手拍上报问题，网格员限时处置，事件流转全程留痕，实现"小事不出村、大事不出镇"。'
    },
    {
      title: '智慧农业助增收',
      desc: '实时监测大棚温湿度、土壤墒情，异常自动告警，结合历史数据给出种植建议，降低农业风险。'
    },
    {
      title: '便民服务到村口',
      desc: '村民手机端查询政策、缴纳费用、预约办事，减少跑腿次数，让数据多跑路、村民少跑腿。'
    },
    {
      title: '产业数据助决策',
      desc: '汇总各村特色产业、合作社、旅游项目数据，乡镇领导一屏掌握产业发展态势，精准制定扶持政策。'
    }
  ],
  highlights: [
    { title: '移动优先', desc: '村干部和村民均可通过手机处理日常事务，降低使用门槛，提高系统活跃度。' },
    { title: '物联融合', desc: '支持对接各类农业传感器、监控设备，实现"看得见、管得住"的数字化管理。' },
    { title: '数据可视化', desc: '乡村关键指标一屏呈现，辅助乡镇党委政府掌握辖区动态、科学决策。' }
  ]
}

export const relatedProducts = [
  {
    icon: '🚩',
    title: '智慧党建',
    desc: '组织生活、党员发展、党费管理、学习培训全流程数字化，助力基层党组织标准化规范化建设。',
    route: '/smart-party',
    color: '#c8102e'
  },
  {
    icon: '👔',
    title: '干部管理',
    desc: '基于AI的干部全生命周期管理平台，涵盖选拔、考核、培养、监督等环节。',
    route: '/cadre-management',
    color: '#0052d9'
  },
  {
    icon: '🛡️',
    title: '综合治理',
    desc: '网格化管理、事件处置、矛盾调解、指挥调度一体化平台，提升基层社会治理效能。',
    route: '/comprehensive-governance',
    color: '#531dab'
  }
]
